/**
 * Todas las escrituras de datos del usuario. Lógica compartida por las dos variantes de UI.
 *
 * Dos invariantes:
 *
 * 1. **Todo se escribe local primero** con `guardarLocal`, que marca la fila como pendiente en
 *    la misma transacción. La red no participa: si no hay señal, no cambia nada del flujo.
 * 2. **La `idem_key` es determinista**: `sesion:ejercicio:numeroSerie`. Es lo que hace que tocar
 *    guardar dos veces no duplique, incluso si el primer intento se cortó a mitad de camino.
 *    Un UUID aleatorio por intento haría imposible reconocer el reintento.
 */

import {
  arrastrarSeries,
  editarSerie,
  moverSesion,
  omitirSerie,
  reasignarEjercicio,
  repetirSerie,
  serieDesdeMarca,
  ultimaMarca,
  type Fecha,
  type Maquina,
  type MedidaCorporal,
  type MotivoOmision,
  type RegistroCardio,
  type Alimento,
  type FuenteComida,
  type MomentoDelDia,
  type Serie,
  type SerieContexto,
  type Sesion,
  type Unidad,
} from '@mg/core';
import type { Perfil } from '@mg/sheets';

import { pedir } from '../datos/api.ts';
import { db, guardarLocal } from '../datos/db.ts';

const nuevoId = (prefijo: string): string => `${prefijo}-${crypto.randomUUID()}`;
const ahora = (): string => new Date().toISOString();

export const idemDeSerie = (sesionId: string, ejercicioId: string, numeroSerie: number): string =>
  `${sesionId}:${ejercicioId}:${numeroSerie}`;

// ---------------------------------------------------------------------------
// Sesiones
// ---------------------------------------------------------------------------

export interface OpcionesAbrirSesion {
  userId: string;
  planId: string | null;
  gimnasioId: string;
  rutinaDiaId: string | null;
  fecha: Fecha;
  /** Ejercicios planificados del día, en orden. */
  ejerciciosPlanificados: string[];
  seriesObjetivo?: number;
  /** Series omitidas por causas ajenas en la sesión anterior, para arrastrarlas (§4.3). */
  omitidasPrevias?: Serie[];
}

/**
 * Abre la sesión y crea las series PENDIENTES del día (completada=false, sin motivo). Tenerlas
 * creadas desde el inicio es lo que permite registrar en dos toques: la fila ya existe, tocar
 * "repetir" sólo la completa.
 */
export async function abrirSesion(o: OpcionesAbrirSesion): Promise<Sesion> {
  const existente = await db.sesiones.where('fecha').equals(o.fecha).first();

  const sesion: Sesion = existente
    ? { ...existente, estado: 'en_curso', iniciada_en: existente.iniciada_en ?? ahora() }
    : {
        id: nuevoId('s'),
        user_id: o.userId,
        plan_id: o.planId,
        rutina_dia_id: o.rutinaDiaId,
        fecha: o.fecha,
        estado: 'en_curso',
        gimnasio_id: o.gimnasioId,
        notas: '',
        iniciada_en: ahora(),
        cerrada_en: null,
      };

  await guardarLocal('sesiones', [sesion]);

  const yaHay = await db.series.where('sesion_id').equals(sesion.id).count();
  if (yaHay === 0) {
    const cuantas = o.seriesObjetivo ?? 3;
    const pendientes: Serie[] = [];

    for (const ejercicioId of o.ejerciciosPlanificados) {
      for (let n = 1; n <= cuantas; n++) {
        pendientes.push({
          id: nuevoId('se'),
          sesion_id: sesion.id,
          ejercicio_id: ejercicioId,
          maquina_id: null,
          numero_serie: n,
          reps: 0,
          peso: 0,
          unidad: 'lb',
          rir: null,
          completada: false,
          motivo_omision: null,
          arrastrada_de_sesion_id: null,
          registrada_en: ahora(),
          idem_key: idemDeSerie(sesion.id, ejercicioId, n),
        });
      }
    }

    // §4.3 — lo que quedó sin hacer por equipo ocupado se suma a esta sesión.
    const arrastradas = arrastrarSeries(o.omitidasPrevias ?? [], {
      sesionDestinoId: sesion.id,
      nuevoId: () => nuevoId('se'),
      ahora: ahora(),
      seriesObjetivo: cuantas,
    });

    await guardarLocal('series', [...pendientes, ...arrastradas]);
  }

  return sesion;
}

export async function cerrarSesion(sesion: Sesion): Promise<void> {
  await guardarLocal('sesiones', [{ ...sesion, estado: 'completada', cerrada_en: ahora() }]);
}

export async function marcarSesionOmitida(sesion: Sesion): Promise<void> {
  await guardarLocal('sesiones', [{ ...sesion, estado: 'omitida', cerrada_en: ahora() }]);
}

/** §4.2 — mover una sesión de fecha arrastra su contenido: las series apuntan a la sesión. */
export async function moverSesionAFecha(sesion: Sesion, nuevaFecha: Fecha): Promise<void> {
  await guardarLocal('sesiones', [moverSesion(sesion, nuevaFecha)]);
}

export async function guardarNotaSesion(sesion: Sesion, notas: string): Promise<void> {
  await guardarLocal('sesiones', [{ ...sesion, notas }]);
}

// ---------------------------------------------------------------------------
// Series — el camino crítico de §4.1
// ---------------------------------------------------------------------------

export interface OpcionesRegistrar {
  sesionId: string;
  ejercicioId: string;
  reps: number;
  peso: number;
  unidad: Unidad;
  maquinaId: string | null;
  rir?: number | null;
  /** Serie pendiente que se está completando. Si falta, se crea una nueva. */
  pendiente?: Serie | null;
  numeroSerie?: number;
}

/**
 * Completa una serie. Si hay una fila pendiente para ese ejercicio, se completa esa; si no, se
 * crea. Escribe y devuelve al instante: la latencia percibida es la de IndexedDB, no la de la
 * red (§9 pide menos de 200 ms).
 */
export async function registrarSerie(o: OpcionesRegistrar): Promise<Serie> {
  const numero =
    o.numeroSerie ??
    o.pendiente?.numero_serie ??
    (await db.series
      .where('sesion_id')
      .equals(o.sesionId)
      .filter((s) => s.ejercicio_id === o.ejercicioId)
      .count()) + 1;

  const base: Serie = o.pendiente ?? {
    id: nuevoId('se'),
    sesion_id: o.sesionId,
    ejercicio_id: o.ejercicioId,
    maquina_id: o.maquinaId,
    numero_serie: numero,
    reps: 0,
    peso: 0,
    unidad: o.unidad,
    rir: null,
    completada: false,
    motivo_omision: null,
    arrastrada_de_sesion_id: null,
    registrada_en: ahora(),
    idem_key: idemDeSerie(o.sesionId, o.ejercicioId, numero),
  };

  const serie: Serie = {
    ...base,
    reps: o.reps,
    peso: o.peso,
    unidad: o.unidad,
    maquina_id: o.maquinaId,
    rir: o.rir ?? null,
    completada: true,
    motivo_omision: null,
    registrada_en: ahora(),
  };

  await guardarLocal('series', [serie]);
  return serie;
}

/**
 * Agrega un ejercicio a una sesión ya abierta, con sus series pendientes.
 *
 * Hace falta porque una sesión puede abrir VACÍA y quedar sin salida: `abrirSesion` planifica los
 * ejercicios del día de rutina, y ese día puede no tener ninguno — o tener ejercicios que el
 * catálogo no conoce, que `useDatos` descarta en silencio al armar `ejerciciosPorDia`. El
 * resultado era una sesión con "0 / 0 series" donde lo único que se podía tocar era el cardio.
 *
 * También sirve para el caso normal: hacer algo que hoy no estaba en el plan.
 *
 * La numeración sigue desde la última serie que ese ejercicio ya tenga en la sesión, así la
 * `idem_key` se mantiene determinista y agregar dos veces no pisa filas anteriores.
 */
export async function agregarEjercicioASesion(
  sesionId: string,
  ejercicioId: string,
  cuantas = 3,
): Promise<Serie[]> {
  const previas = await db.series
    .where('sesion_id')
    .equals(sesionId)
    .filter((s) => s.ejercicio_id === ejercicioId)
    .toArray();

  const desde = previas.reduce((max, s) => Math.max(max, s.numero_serie), 0);

  const nuevas: Serie[] = Array.from({ length: cuantas }, (_, i) => {
    const numero = desde + i + 1;
    return {
      id: nuevoId('se'),
      sesion_id: sesionId,
      ejercicio_id: ejercicioId,
      maquina_id: null,
      numero_serie: numero,
      reps: 0,
      peso: 0,
      unidad: 'lb',
      rir: null,
      completada: false,
      motivo_omision: null,
      arrastrada_de_sesion_id: null,
      registrada_en: ahora(),
      idem_key: idemDeSerie(sesionId, ejercicioId, numero),
    };
  });

  await guardarLocal('series', nuevas);
  return nuevas;
}

/** Quita de la sesión las series pendientes de un ejercicio agregado por error. */
export async function quitarEjercicioDeSesion(sesionId: string, ejercicioId: string): Promise<void> {
  const pendientes = await db.series
    .where('sesion_id')
    .equals(sesionId)
    .filter((s) => s.ejercicio_id === ejercicioId && !s.completada && s.motivo_omision === null)
    .toArray();

  await db.transaction('rw', db.series, db.sucios, async () => {
    await db.series.bulkDelete(pendientes.map((s) => s.id));
    for (const s of pendientes) await db.sucios.delete(`series:${s.id}`);
  });
}

/**
 * Agrega una serie MÁS al ejercicio, copiando la anterior.
 *
 * OJO: no consume las series pendientes, así que sólo sirve cuando no queda ninguna. Con
 * pendientes vivas numera `referencia + 1`, que es justo el número de una pendiente que sigue
 * existiendo: dos filas con la misma `idem_key`, y el objetivo creciendo con lo hecho (1/3 → 2/4).
 * El ＋ de Entrenar pasa por `registrarSerie`, que completa la pendiente cuando la hay.
 */
export async function repetirUltimaSerie(sesionId: string, ejercicioId: string): Promise<Serie | null> {
  const delEjercicio = (
    await db.series
      .where('sesion_id')
      .equals(sesionId)
      .filter((s) => s.ejercicio_id === ejercicioId && s.completada)
      .toArray()
  ).sort((a, b) => b.numero_serie - a.numero_serie);

  const referencia = delEjercicio[0];
  if (!referencia) return null;

  const serie = repetirSerie(referencia, {
    sesionId,
    numeroSerie: referencia.numero_serie + 1,
    nuevoId: () => nuevoId('se'),
    ahora: ahora(),
  });

  await guardarLocal('series', [serie]);
  return serie;
}

/** Precarga a partir del historial: peso y reps de la última vez (§4.1). */
export function precargaDe(
  contextos: SerieContexto[],
  ejercicioId: string,
): { peso: number; reps: number; unidad: Unidad; maquinaId: string | null; fecha: Fecha } | null {
  const marca = ultimaMarca(contextos, ejercicioId);
  if (!marca) return null;
  return {
    peso: marca.referencia.peso,
    reps: marca.referencia.reps,
    unidad: marca.referencia.unidad,
    maquinaId: marca.maquina_id,
    fecha: marca.fecha,
  };
}

export async function crearSerieDesdeHistorial(
  contextos: SerieContexto[],
  sesionId: string,
  ejercicioId: string,
  numeroSerie: number,
): Promise<Serie> {
  const serie = serieDesdeMarca(ultimaMarca(contextos, ejercicioId), {
    sesionId,
    ejercicioId,
    numeroSerie,
    nuevoId: () => nuevoId('se'),
    ahora: ahora(),
  });
  await guardarLocal('series', [serie]);
  return serie;
}

export async function editarSerieLocal(
  serie: Serie,
  cambios: Partial<Pick<Serie, 'reps' | 'peso' | 'unidad' | 'rir'>>,
): Promise<void> {
  await guardarLocal('series', [editarSerie(serie, cambios)]);
}

/** §4.2 — reasignar sin borrar y recrear: conserva id e idem_key. */
export async function reasignarSerieLocal(
  serie: Serie,
  nuevoEjercicioId: string,
  nuevaMaquinaId: string | null,
): Promise<void> {
  await guardarLocal('series', [reasignarEjercicio(serie, nuevoEjercicioId, nuevaMaquinaId)]);
}

/** §4.3 — omitir con motivo. Marca todas las series de ese ejercicio en la sesión. */
export async function omitirEjercicio(
  sesionId: string,
  ejercicioId: string,
  motivo: MotivoOmision,
): Promise<void> {
  const delEjercicio = await db.series
    .where('sesion_id')
    .equals(sesionId)
    .filter((s) => s.ejercicio_id === ejercicioId && !s.completada)
    .toArray();

  if (delEjercicio.length === 0) return;
  // Una sola fila omitida por ejercicio: tres filas de "no lo hice" no aportan nada y ensucian
  // el historial. Se conserva la primera y se descartan las demás pendientes.
  const [primera, ...resto] = delEjercicio;
  await guardarLocal('series', [omitirSerie(primera!, motivo)]);
  if (resto.length > 0) await db.series.bulkDelete(resto.map((s) => s.id));
}

export async function borrarSerie(serie: Serie): Promise<void> {
  await db.transaction('rw', db.series, db.sucios, async () => {
    await db.series.delete(serie.id);
    // Se deja marcada para que el push le informe al servidor... pero el servidor no borra
    // filas por sync. El borrado real se hace desde la planilla, a mano. Es deliberado: en una
    // app de registro, perder un dato por un toque accidental es peor que tener una fila de más.
    await db.sucios.delete(`series:${serie.id}`);
  });
}

// ---------------------------------------------------------------------------
// Cardio
// ---------------------------------------------------------------------------

export async function guardarCardio(
  sesionId: string,
  datos: Omit<RegistroCardio, 'id' | 'sesion_id' | 'idem_key'>,
  registroExistente?: RegistroCardio | null,
): Promise<RegistroCardio> {
  const registro: RegistroCardio = registroExistente
    ? { ...registroExistente, ...datos }
    : { ...datos, id: nuevoId('c'), sesion_id: sesionId, idem_key: `${sesionId}:cardio` };
  await guardarLocal('cardio', [registro]);
  return registro;
}

// ---------------------------------------------------------------------------
// Medidas corporales
// ---------------------------------------------------------------------------

export async function guardarMedida(
  datos: Omit<MedidaCorporal, 'id' | 'idem_key'>,
  existente?: MedidaCorporal | null,
): Promise<MedidaCorporal> {
  const medida: MedidaCorporal = existente
    ? { ...existente, ...datos }
    : {
        ...datos,
        id: nuevoId('md'),
        // Una medición por fecha y hora: volver a confirmar la misma foto no crea una segunda.
        idem_key: `${datos.fecha}T${datos.hora}`,
      };
  await guardarLocal('medidas', [medida]);
  return medida;
}

export async function borrarMedida(medida: MedidaCorporal): Promise<void> {
  await db.medidas.delete(medida.id);
  await db.sucios.delete(`medidas:${medida.id}`);
}

/** §4.5.4 — deja la trazabilidad del OCR: JSON crudo y confianza, junto al dato confirmado. */
export async function guardarIngesta(datos: {
  userId: string;
  tipo: 'balanza' | 'consola_cardio';
  jsonCrudo: string;
  confianza: number;
  medidaId?: string | null;
  cardioId?: string | null;
}): Promise<void> {
  await guardarLocal('ingestas_imagen', [
    {
      id: nuevoId('ing'),
      user_id: datos.userId,
      tipo: datos.tipo,
      // La imagen no se guarda: lo auditable es lo que el modelo LEYÓ, y guardar fotos de una
      // balanza es guardar datos de salud sin necesidad.
      imagen_ref: '',
      json_crudo: datos.jsonCrudo,
      confianza: datos.confianza,
      confirmado_por_usuario: true,
      medida_id: datos.medidaId ?? null,
      cardio_id: datos.cardioId ?? null,
      creada_en: ahora(),
    },
  ]);
}

// ---------------------------------------------------------------------------
// Comidas
// ---------------------------------------------------------------------------

/**
 * Guarda los alimentos de una comida. Una fila por alimento, así se puede corregir uno sin tocar
 * los demás.
 *
 * La `idem_key` incluye el índice dentro de la comida: confirmar dos veces la misma estimación no
 * duplica los alimentos, igual que tocar guardar dos veces no duplica una serie.
 */
export async function guardarAlimentos(
  userId: string,
  comun: { fecha: Fecha; hora: string; momento: MomentoDelDia; fuente: FuenteComida },
  alimentos: {
    descripcion: string;
    kcal: number;
    proteina_g: number;
    carbohidratos_g: number | null;
    grasa_g: number | null;
    fibra_g: number | null;
    confianza: number | null;
  }[],
): Promise<Alimento[]> {
  const filas: Alimento[] = alimentos.map((a, i) => ({
    id: nuevoId('al'),
    user_id: userId,
    fecha: comun.fecha,
    hora: comun.hora,
    momento: comun.momento,
    descripcion: a.descripcion,
    kcal: a.kcal,
    proteina_g: a.proteina_g,
    carbohidratos_g: a.carbohidratos_g,
    grasa_g: a.grasa_g,
    fibra_g: a.fibra_g,
    confianza: a.confianza,
    fuente: comun.fuente,
    idem_key: `${comun.fecha}T${comun.hora}:${comun.momento}:${i}`,
  }));

  await guardarLocal('comidas', filas);
  return filas;
}

export async function editarAlimento(alimento: Alimento, cambios: Partial<Alimento>): Promise<void> {
  await guardarLocal('comidas', [{ ...alimento, ...cambios }]);
}

export async function borrarAlimento(alimento: Alimento): Promise<void> {
  await db.comidas.delete(alimento.id);
  await db.sucios.delete(`comidas:${alimento.id}`);
}

// ---------------------------------------------------------------------------
// Plan y calendario
// ---------------------------------------------------------------------------

export async function bloquearDias(
  userId: string,
  gimnasioId: string,
  fechas: Fecha[],
  motivo: string,
): Promise<void> {
  await guardarLocal(
    'dias_bloqueados',
    fechas.map((fecha) => ({
      id: `b-${userId}-${fecha}`,
      user_id: userId,
      gimnasio_id: gimnasioId,
      fecha,
      motivo,
    })),
  );
}

export async function desbloquearDia(id: string): Promise<void> {
  await db.diasBloqueados.delete(id);
  await db.sucios.delete(`dias_bloqueados:${id}`);
}

export async function guardarPlan(plan: Parameters<typeof guardarLocal>[1][number]): Promise<void> {
  await guardarLocal('planes', [plan as { id: string }]);
}

// ---------------------------------------------------------------------------
// Perfil
// ---------------------------------------------------------------------------

export async function guardarPerfil(perfil: Perfil): Promise<void> {
  await guardarLocal('perfil', [perfil as unknown as { id: string }]);
}

// ---------------------------------------------------------------------------
// Mapa de máquinas compartido (§2) — esto NO es local-first
// ---------------------------------------------------------------------------

/**
 * El mapa de máquinas es del gimnasio, no del usuario, así que va directo al servidor. Sin
 * conexión no se puede aportar: guardarlo local y sincronizarlo después crearía la ilusión de
 * que otros usuarios ya lo ven.
 */
export async function aportarMaquina(maquina: {
  id?: string;
  gimnasio_id: string;
  etiqueta_ubicacion: string;
  ejercicio_id: string;
  unidad_placa: Unidad;
  incremento_placa: number;
  placa_min?: number | null;
  placa_max?: number | null;
}): Promise<void> {
  await pedir('/catalogo/maquinas', { metodo: 'POST', cuerpo: { maquinas: [maquina] } });
}

/**
 * Anotar dónde está una máquina desde la pantalla de registro, sin ir hasta Ajustes.
 *
 * "Ubicación sin registrar" era un dato faltante sin salida: aparecía en cada tarjeta y no decía
 * ni qué era ni cómo arreglarlo. Es el mismo aporte que hace Ajustes, con lo mínimo que el
 * servidor exige — el resto (mínimo, máximo) se completa después, si alguien quiere.
 *
 * El id se genera acá para poder copiar la fila en local apenas el servidor la aceptó: así la
 * etiqueta aparece donde el usuario la escribió, en vez de esperar al próximo pull. No se marca
 * como sucia porque el mapa compartido no se sincroniza por push (tiene su propio endpoint) y
 * `pull` reemplaza esa tabla entera.
 */
export async function anotarUbicacionDeMaquina(datos: {
  gimnasioId: string;
  ejercicioId: string;
  etiqueta: string;
  unidad: Unidad;
  /** Null = no lo sabe: queda el valor por defecto del catálogo hasta que alguien lo mida. */
  incremento: number | null;
}): Promise<Maquina> {
  const fila: Maquina = {
    id: nuevoId('m'),
    gimnasio_id: datos.gimnasioId,
    etiqueta_ubicacion: datos.etiqueta.trim(),
    ejercicio_id: datos.ejercicioId,
    unidad_placa: datos.unidad,
    incremento_placa: datos.incremento !== null && datos.incremento > 0 ? datos.incremento : 5,
    placa_min: null,
    placa_max: null,
  };

  await aportarMaquina(fila);
  await db.maquinas.put(fila);
  return fila;
}

export async function aportarGimnasio(gimnasio: {
  id?: string;
  nombre: string;
  notas: string;
  dias_cierre_semanal: number[];
}): Promise<void> {
  await pedir('/catalogo/gimnasios', { metodo: 'POST', cuerpo: { gimnasios: [gimnasio] } });
}
